import React, { useState, useEffect, useRef } from 'react';
import { useDebounce } from 'react-use';
import { Formik } from 'formik';

import { DialogContent, Box } from '@mui/material';

import { useTranslation } from 'next-i18next';

import Dialog from 'src/components/ui/Dialog';
import DialogTitle from 'src/components/ui/DialogTitle';
import Typography from 'src/components/ui/Typography';
import Icon from 'src/components/ui/Icon';
import Loader from 'src/components/ui/Loader';
import SimpleHorizontalUserCard from 'src/components/ui/SimpleHorizontalUserCard';
import TextField from 'src/components/ui/TextField';
import IconButton from 'src/components/ui/IconButton';

import { useCompanyMembersWhoAreInMyContactsQuery } from 'src/hooks/__generated__/queries';
import type { CompanyMembersWhoAreInMyContactsQuery, User } from 'src/types/__generated__/graphql';

import useActivityIdSearch from 'src/hooks/useActivityIdSearch';
import useAlerts from 'src/hooks/useAlerts';
import type { ModalProps } from 'src/hooks/useModalOpenState';

import theme from 'src/utils/theme';
import { isBottomDiv } from 'src/utils/isBottom';
import { styledComponent, styledTag } from 'src/utils/styled';
import { colours } from 'src/constants/colours';

const StyledDialog = styledComponent(Dialog)`
    .MuiDialog-paper {
        width: 24rem;
    }
    .MuiDialogContent-root {
        padding: 0 1.5rem;
    }
    .MuiDialogActions-root {
        padding: 0 1.5rem 1.5rem 1.5rem;
    }
    .MuiDialogTitle-root {
        padding: 1.5rem 1.5rem 0 1.5rem;
    }
`;

const StyledCardContainer = styledTag('div')`
    border-bottom: 0.0625rem solid ${theme.palette.divider};
    margin-left: -0.5rem;
`;

const TextFieldWrapper = styledComponent(Box)`
    margin-left: -1.5rem;
    width: calc(100% + 3rem);
    padding: 0 1.5rem;
    box-shadow: 0 0.625rem 1.25rem ${colours.blackOpacity.O08};
    z-index: 10;
`;

StyledDialog.defaultProps = {
    fullWidth: true,
    maxWidth: 'xs',
};

interface Props extends ModalProps {
    companyId: string;
    mutualContactsCount: number;
}

export type ViewTypes = 'relationsList' | 'searchProfile';
type ViewTypeTitles = Record<ViewTypes, string>;

const SearchCompanyMutualContactsModal: React.FC<Props> = ({ open, onClose, companyId, mutualContactsCount }) => {
    const { t } = useTranslation(['profile']);
    const { addAlert } = useAlerts();
    const [query, setQuery] = useState('');
    const [debouncedQuery, setDebouncedQuery] = useState('');
    const scrollerContainerRef = useRef<HTMLDivElement | null>(null);

    const [selectedView, setSelectedView] = useState<ViewTypes>('relationsList');

    const titles: ViewTypeTitles = {
        relationsList: t('company:relationsWhoWorkHereModal', {
            plurial: mutualContactsCount > 1 ? 's' : '',
            verbPlurial: mutualContactsCount > 1 ? 'nt' : '',
        }),
        searchProfile: t('common:searchProfile'),
    };

    useDebounce(
        () => {
            setDebouncedQuery(query);
            if (scrollerContainerRef.current) {
                scrollerContainerRef.current.scrollTop = 0;
            }
        },
        500,
        [query],
    );

    const activityIds = useActivityIdSearch(debouncedQuery, t);

    const defaultTake = 10;
    const {
        data: searchContacts,
        fetchMore,
        loading,
    } = useCompanyMembersWhoAreInMyContactsQuery({
        variables: {
            companyId,
            skip: 0,
            take: defaultTake,
            query: debouncedQuery.length > 0 ? debouncedQuery : null,
            activityIds,
        },
        skip: !open,
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true,
    });

    useEffect(() => {
        if (scrollerContainerRef.current) {
            scrollerContainerRef.current.scrollTop = 0;
        }
    }, [selectedView]);

    const handleScroll = async (ev: React.UIEvent<HTMLDivElement, UIEvent>) => {
        const bottom = isBottomDiv(ev.currentTarget);

        const actualDataLength = searchContacts?.companyMembersWhoAreInMyContacts?.results?.length ?? 0;
        const allDataLoaded = actualDataLength >= (searchContacts?.companyMembersWhoAreInMyContacts?.total ?? 0);

        if (bottom && !loading && !allDataLoaded && actualDataLength) {
            try {
                await fetchMore({
                    variables: {
                        skip: actualDataLength,
                    },
                    updateQuery(
                        prev,
                        { fetchMoreResult }: { fetchMoreResult?: CompanyMembersWhoAreInMyContactsQuery },
                    ) {
                        if (!fetchMoreResult) return prev;
                        const prevItems = prev.companyMembersWhoAreInMyContacts?.results ?? [];
                        const newItems = fetchMoreResult.companyMembersWhoAreInMyContacts?.results ?? [];
                        return {
                            companyMembersWhoAreInMyContacts: {
                                ...prev.companyMembersWhoAreInMyContacts,
                                results: [...prevItems, ...newItems],
                            },
                        };
                    },
                });
            } catch (error: unknown) {
                addAlert({
                    type: 'error',
                    message: (error as Error).message,
                });
            }
        }
    };

    return (
        <StyledDialog open={open} onClose={onClose} fullscreenOnMobile>
            <DialogTitle onClose={onClose}>
                <Box
                    width="100%"
                    display="flex"
                    justifyContent={selectedView === 'relationsList' ? 'space-between' : 'start'}
                    mr="1.5rem"
                    alignItems="center"
                >
                    {selectedView === 'searchProfile' && (
                        <Box mr="1rem">
                            <Icon name="ArrowBack" onClick={() => setSelectedView('relationsList')} />
                        </Box>
                    )}
                    <Typography variant="h2" serif>
                        {titles[selectedView]}
                    </Typography>
                    {selectedView === 'relationsList' && (
                        <IconButton
                            onClick={() => setSelectedView('searchProfile')}
                            color="white"
                            style={{
                                backgroundColor: 'transparent',
                            }}
                            iconProps={{
                                name: 'Search',
                            }}
                        />
                    )}
                </Box>
            </DialogTitle>
            <DialogContent>
                {mutualContactsCount === 0 ? (
                    <Box
                        display="flex"
                        flexDirection="column"
                        mt="1.5rem"
                        height="25rem"
                        overflow="auto"
                        width="100%"
                        justifyContent="center"
                        alignItems="center"
                    >
                        <Icon size="6.5rem" name="NoCommonRelations" />
                        <Box maxWidth="8.4rem" mt="1rem">
                            <Typography variant="body2" align="center">
                                {t('profile:noCommonRelations')}
                            </Typography>
                        </Box>
                    </Box>
                ) : (
                    <>
                        {selectedView === 'searchProfile' && (
                            <Formik initialValues={{ query: '' }} onSubmit={console.info}>
                                {/* Formik is used here to avoid errors using useField in children components */}
                                <TextFieldWrapper>
                                    <TextField
                                        data-target-id="search-company-recommendations-modal"
                                        name="search"
                                        label={t('common:search')}
                                        InputProps={{
                                            startAdornment: <Icon name="Search" />,
                                        }}
                                        onChange={(e) => setQuery(e.target.value)}
                                    />
                                </TextFieldWrapper>
                            </Formik>
                        )}
                        <Box
                            ref={scrollerContainerRef}
                            display="flex"
                            flexDirection="column"
                            mb="1.5rem"
                            mt="0.5rem"
                            height="25rem"
                            overflow="auto"
                            onScroll={handleScroll}
                        >
                            {searchContacts?.companyMembersWhoAreInMyContacts?.results &&
                                searchContacts.companyMembersWhoAreInMyContacts.results.length > 0 &&
                                searchContacts.companyMembersWhoAreInMyContacts.results.map(
                                    (contact) =>
                                        contact && (
                                            <StyledCardContainer>
                                                <SimpleHorizontalUserCard user={contact as User} />
                                            </StyledCardContainer>
                                        ),
                                )}
                            {loading && <Loader />}
                        </Box>
                    </>
                )}
            </DialogContent>
        </StyledDialog>
    );
};

export default SearchCompanyMutualContactsModal;
